// Space on a node for other people (HRTH-182, docs/STORAGE.md §11.3): the owner's upload limits
// for people, spaces and roles, sealed under the node location's key so only their devices and
// the node read them; the server learns who they name. The node answers questions about its
// room sealed for whoever asks (`hrth_core::node`).
import type { Api } from '../api/http'
import type { NodeOpenTo } from '../api/types'
import { nodeAnswerKeypair, nodeAnswerOpen } from '../core/wasm'
import { decryptName, encryptName } from './metacrypto'
import { formatBytes } from './pattern'

/** Upload limits in MiB by user id, space id and role id; null is no limit, 0 shuts someone out. */
export interface NodeRules {
  people: Record<string, number | null>
  spaces: Record<string, number | null>
  roles: Record<string, number | null>
}

export const noRules = (): NodeRules => ({ people: {}, spaces: {}, roles: {} })

/** What a node lets someone keep. */
export type Allowance = 'none' | 'unlimited' | { bytes: number }

/** A node's answer about its room for me. */
export interface NodeAnswer {
  name: string | null
  allowance: Allowance
  used: number
}

const kind = (location: string) => `node-rules:${location}` as const

export const sealRules = (key: string, location: string, rules: NodeRules) =>
  encryptName(key, kind(location), JSON.stringify(rules))

/** My node's limits, opened with its key (none if it isn't open, or the key is missing). */
export async function openRules(key: string | undefined, location: string, sealed: string | undefined): Promise<NodeRules> {
  if (!key || !sealed) return noRules()
  try {
    const r = JSON.parse((await decryptName(key, kind(location), sealed)) ?? '{}') as Partial<NodeRules>
    return { people: r.people ?? {}, spaces: r.spaces ?? {}, roles: r.roles ?? {} }
  } catch {
    return noRules()
  }
}

/** Who the limits name, for the server. */
export const openTo = (r: NodeRules): NodeOpenTo => ({
  users: Object.keys(r.people),
  spaces: Object.keys(r.spaces),
  roles: Object.keys(r.roles),
})

export const isOpen = (r: NodeRules) => Object.keys(r.people).length + Object.keys(r.spaces).length + Object.keys(r.roles).length > 0

/** Asks a node about its room for me. */
export async function askRoom(api: Pick<Api, 'askNode'>, location: string): Promise<NodeAnswer> {
  const [secret = '', pub = ''] = nodeAnswerKeypair().split('.')
  const { sealed } = await api.askNode(location, pub)
  const json = nodeAnswerOpen(secret, sealed)
  if (!json) throw new Error('The node’s answer didn’t open')
  return JSON.parse(json) as NodeAnswer
}

/** Bytes left, or null for no limit (0 when shut out). */
export function bytesLeft(a: NodeAnswer): number | null {
  if (a.allowance === 'unlimited') return null
  if (a.allowance === 'none') return 0
  return Math.max(0, a.allowance.bytes - a.used)
}

/** "3.2 GB left", "no limit", or "no space for you". */
export function roomText(a: NodeAnswer): string {
  if (a.allowance === 'none') return 'no space for you'
  const left = bytesLeft(a)
  return left == null ? 'no limit' : `${formatBytes(left)} left`
}

/** A limit as typed in GB ("" no limit) into MiB; undefined if it isn't a number of 0 or more. */
export function limitFromText(text: string): number | null | undefined {
  const t = text.trim()
  if (!t) return null
  const gb = Number(t)
  return Number.isFinite(gb) && gb >= 0 ? Math.round(gb * 1024) : undefined
}

/** MiB as GB for an input ("" for no limit). */
export const limitText = (mb: number | null) => (mb == null ? '' : String(Math.round((mb / 1024) * 100) / 100))
